import {
  MAX_APPROVAL_LABEL_CHARS,
  MAX_APPROVAL_TYPE_LABEL_CHARS,
  isApprovalEffect,
  type ApprovalEffect,
  type ApprovalFailureCode,
  type ApprovalOperation,
  type ApprovalPlan,
  type ApprovalRequest,
  type ApprovalResult,
  type ServerMessage,
} from 'ts-components/PiChat/protocol';
import {
  RESULT_DEADLINE_MARGIN_MS,
  resolveResultUpload,
  type ResolvedResultUpload,
} from 'ts-components/PiChat/presignedPut';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import { SESSION_ENDING_ERROR_CODES } from 'ts-components/PiChat/reducer';

/** Model-facing result messages (each well under MAX_APPROVAL_MESSAGE_CHARS); not shown to the user. */
export const APPROVAL_MESSAGES = {
  invalid: 'The approval request was malformed',
  noTimeToConfirm: 'The approval request expired before the user answered',
  busy: 'Another approval is still waiting for the user',
  ended: 'The chat closed before the user answered',
} as const;

export type ApprovalFrame = Extract<ServerMessage, { type: 'ui.approval' }>;

const LABEL = 'ui.approval';
const TOO_LARGE: ApprovalResult = { ok: false, code: 'unavailable' };

const approvalFailure = (
  code: ApprovalFailureCode,
  message?: string
): ApprovalResult =>
  message === undefined ? { ok: false, code } : { ok: false, code, message };

export interface ValidApprovalRequest {
  requestId: string;
  allowWindow: boolean;
  windowMinutes: number;
  request: ApprovalRequest;
}

export type ApprovalControllerState =
  | { status: 'idle' }
  | ({ status: 'pending' } & ValidApprovalRequest);

export interface ApprovalControllerDeps {
  /** Resolves `stored` once the server can read the result (`sendResultUpload`). */
  sendResult(
    upload: ResolvedResultUpload<ApprovalResult>,
    result: ApprovalResult
  ): Promise<'stored' | 'failed' | 'expired'>;
}

/**
 * `failed`: a grant whose result never reached the server (the upload failed
 * or its URL expired), so nothing was approved.
 */
export type SettledOutcome =
  | 'approved'
  | 'approved_window'
  | 'declined'
  | 'cancelled'
  | 'expired'
  | 'failed';
/** `sending`: a grant whose result is still uploading; it shows as approved only once stored. */
export type ApprovalOutcome = 'pending' | 'sending' | SettledOutcome;

/** One card in the chat stream: every request that reached the user, pending or settled. */
export interface ApprovalCardRecord extends ValidApprovalRequest {
  toolCallId: string;
  outcome: ApprovalOutcome;
}

export interface ApprovalController {
  handle(frame: ApprovalFrame, meta: PiChatFrameMeta): void;
  approveOnce(): void;
  /** A no-op unless the pending request allows a window grant. */
  approveWindow(): void;
  decline(): void;
  /** The user stopped the turn: answers `declined` and marks the card cancelled. */
  cancel(): void;
  /** The request can no longer be answered by the user (turn over, connection gone). */
  end(): void;
  /**
   * The session on this connection ended (closed, or an error that closes
   * it): later requests on it are answered `unavailable` with no card, until
   * `sessionStarted` on that connection or a new connection.
   */
  sessionEnded(meta: PiChatFrameMeta): void;
  sessionStarted(meta: PiChatFrameMeta): void;
  dispose(): void;
  getState(): ApprovalControllerState;
  /** Cards in arrival order; a new array only when one is added or settles. */
  getCards(): readonly ApprovalCardRecord[];
  subscribe(listener: () => void): () => void;
}

/** The card's settled state for the result sent. */
export const approvalOutcome = (result: ApprovalResult): ApprovalOutcome => {
  if (result.ok)
    return result.grant === 'window' ? 'approved_window' : 'approved';
  return result.code === 'declined' ? 'declined' : 'expired';
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isString = (v: unknown): v is string => typeof v === 'string';

const isNonEmptyString = (v: unknown): v is string =>
  isString(v) && v.length > 0;

const isCount = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;

/** One clipped line, or undefined for anything that isn't a non-blank string. */
const oneLine = (v: unknown, max: number): string | undefined => {
  if (!isString(v)) return undefined;
  const line = v.replace(/\s+/g, ' ').trim();
  if (!line) return undefined;
  return line.length > max ? `${line.slice(0, max)}…` : line;
};

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** One identifier (`first_used`): not a path (`a.b`), a range, an id or anything with spaces. */
const isApiName = (v: string) => /^[A-Za-z0-9_-]+$/.test(v) && !UUID.test(v);

const optionalLabels = (v: Record<string, unknown>) => {
  const label = oneLine(v.label, MAX_APPROVAL_LABEL_CHARS);
  const apiName = oneLine(v.apiName, MAX_APPROVAL_LABEL_CHARS);
  const parentLabel = oneLine(v.parentLabel, MAX_APPROVAL_LABEL_CHARS);
  const parentKey = oneLine(v.parentKey, MAX_APPROVAL_LABEL_CHARS);
  const typeLabel = oneLine(v.typeLabel, MAX_APPROVAL_TYPE_LABEL_CHARS);
  return {
    ...(label === undefined ? {} : { label }),
    // One identifier only: a path, range or id is dropped, never shown as a name.
    ...(apiName === undefined || !isApiName(apiName) ? {} : { apiName }),
    ...(parentLabel === undefined ? {} : { parentLabel }),
    ...(parentKey === undefined ? {} : { parentKey }),
    ...(typeLabel === undefined ? {} : { typeLabel }),
  };
};

const parseOperation = (v: unknown): ApprovalOperation | null => {
  if (
    !isRecord(v) ||
    !isNonEmptyString(v.action) ||
    !isString(v.kind) ||
    !isString(v.key) ||
    typeof v.destructive !== 'boolean' ||
    (v.detail !== undefined && !isString(v.detail))
  ) {
    return null;
  }
  const operation: ApprovalOperation = {
    action: v.action,
    kind: v.kind,
    key: v.key,
    destructive: v.destructive,
  };
  return {
    ...operation,
    ...(isString(v.detail) ? { detail: v.detail } : {}),
    // An unknown or malformed effect (older or newer server) is dropped, not refused.
    ...(isApprovalEffect(v.effect) ? { effect: v.effect } : {}),
    // Display names are optional: a malformed one is dropped, never the frame.
    ...optionalLabels(v),
  };
};

const parsePlan = (v: unknown): ApprovalPlan | null => {
  if (
    !isRecord(v) ||
    !isNonEmptyString(v.planId) ||
    !isString(v.summary) ||
    !Array.isArray(v.operations) ||
    !isRecord(v.counts)
  ) {
    return null;
  }
  const { total, destructive } = v.counts;
  const operations = v.operations.map(parseOperation);
  if (
    !isCount(total) ||
    !isCount(destructive) ||
    destructive > total ||
    operations.length > total ||
    !operations.every((op): op is ApprovalOperation => op !== null) ||
    // Destructive operations are never truncated: every one the counts claim
    // must be listed, or the user would approve a change they can't see.
    operations.filter((op) => op.destructive).length !== destructive
  ) {
    return null;
  }
  return {
    planId: v.planId,
    summary: v.summary,
    operations,
    counts: { total, destructive },
  };
};

const parseRequest = (v: unknown): ApprovalRequest | null => {
  if (!isRecord(v)) return null;
  switch (v.kind) {
    case 'plans': {
      if (!Array.isArray(v.plans) || v.plans.length === 0) return null;
      const plans = v.plans.map(parsePlan);
      return plans.every((plan): plan is ApprovalPlan => plan !== null)
        ? { kind: 'plans', plans }
        : null;
    }
    case 'command':
      return Array.isArray(v.argv) &&
        v.argv.length > 0 &&
        v.argv.every(isString)
        ? { kind: 'command', argv: [...v.argv] }
        : null;
    default:
      return null;
  }
};

/**
 * Total guard over an untrusted `ui.approval` frame: a normalized copy of the
 * fields the card shows, or null (never throws) when any is malformed.
 */
export const parseApprovalFrame = (
  frame: unknown
): ValidApprovalRequest | null => {
  if (
    !isRecord(frame) ||
    !isNonEmptyString(frame.requestId) ||
    !isNonEmptyString(frame.toolCallId) ||
    typeof frame.allowWindow !== 'boolean' ||
    typeof frame.windowMinutes !== 'number' ||
    !Number.isFinite(frame.windowMinutes) ||
    (frame.allowWindow && frame.windowMinutes <= 0)
  ) {
    return null;
  }
  const request = parseRequest(frame.request);
  return request
    ? {
        requestId: frame.requestId,
        allowWindow: frame.allowWindow,
        windowMinutes: frame.windowMinutes,
        request,
      }
    : null;
};

/** The upload, when the frame carries one shaped well enough to answer on. */
const hasResultUpload = (
  frame: unknown
): frame is Pick<ApprovalFrame, 'upload'> =>
  isRecord(frame) &&
  isRecord(frame.upload) &&
  typeof frame.upload.expiresAt === 'number' &&
  isRecord(frame.upload.result) &&
  isString(frame.upload.result.url) &&
  isRecord(frame.upload.result.headers) &&
  Object.values(frame.upload.result.headers).every(isString) &&
  typeof frame.upload.result.maxBytes === 'number';

const IDLE: ApprovalControllerState = { status: 'idle' };

interface Pending {
  requestId: string;
  upload: ResolvedResultUpload<ApprovalResult>;
  allowWindow: boolean;
  /** The connection the request arrived on; approving re-checks it is still current. */
  meta: PiChatFrameMeta;
  timer: ReturnType<typeof setTimeout> | undefined;
  onAbort: () => void;
}

export const createApprovalController = (
  deps: ApprovalControllerDeps
): ApprovalController => {
  const listeners = new Set<() => void>();
  let state: ApprovalControllerState = IDLE;
  let pending: Pending | null = null;
  let cards: readonly ApprovalCardRecord[] = [];
  const seen = new Set<string>();
  /** Connections (by their signal) whose session has ended; a new connection has a new signal. */
  const endedSessions = new WeakSet<AbortSignal>();

  const notify = () => listeners.forEach((listener) => listener());

  /** Moves a card from `from` to `outcome`; a card already elsewhere is left alone. */
  const moveCard = (
    requestId: string,
    from: ApprovalOutcome,
    outcome: ApprovalOutcome
  ) => {
    cards = cards.map((card) =>
      card.requestId === requestId && card.outcome === from
        ? { ...card, outcome }
        : card
    );
  };

  const finish = (
    result: (taken: Pending) => ApprovalResult,
    outcome?: ApprovalOutcome
  ) => {
    const taken = pending;
    if (!taken) return;
    pending = null;
    clearTimeout(taken.timer);
    taken.meta.signal.removeEventListener('abort', taken.onAbort);
    const body = result(taken);
    const settled = outcome ?? approvalOutcome(body);
    const granted = settled === 'approved' || settled === 'approved_window';
    // A grant shows as approved only once the server can read it.
    moveCard(taken.requestId, 'pending', granted ? 'sending' : settled);
    state = IDLE;
    notify();
    const sent = deps.sendResult(taken.upload, body);
    if (!granted) return;
    sent
      .then(
        (status) => status === 'stored',
        () => false
      )
      .then((stored) => {
        moveCard(taken.requestId, 'sending', stored ? settled : 'failed');
        notify();
      });
  };

  const ended = () => approvalFailure('unavailable', APPROVAL_MESSAGES.ended);
  const noTime = () =>
    approvalFailure('unavailable', APPROVAL_MESSAGES.noTimeToConfirm);

  // A grant counts only while the connection that asked is the live one, and
  // before the deadline: the expiry timer can fire late (sleep, a background tab).
  const grant = (kind: 'once' | 'window') => (taken: Pending) => {
    if (taken.meta.signal.aborted || !taken.meta.isCurrent()) return ended();
    return Date.now() >= taken.upload.expiresAt - RESULT_DEADLINE_MARGIN_MS
      ? noTime()
      : ({ ok: true, grant: kind } as const);
  };

  return {
    handle(frame, meta) {
      if (meta.signal.aborted || !meta.isCurrent()) return;
      if (!hasResultUpload(frame)) return;
      const upload = resolveResultUpload(
        frame.upload,
        meta.url,
        LABEL,
        TOO_LARGE
      );
      if (!upload || Date.now() >= upload.expiresAt) return;
      // A repeated requestId (a resend, or a replay after settling) was already answered.
      const requestId = (frame as { requestId?: unknown }).requestId;
      if (isNonEmptyString(requestId)) {
        if (seen.has(requestId)) return;
        seen.add(requestId);
      }
      if (endedSessions.has(meta.signal)) {
        deps.sendResult(upload, ended());
        return;
      }
      const valid = parseApprovalFrame(frame);
      if (!valid) {
        deps.sendResult(
          upload,
          approvalFailure('unavailable', APPROVAL_MESSAGES.invalid)
        );
        return;
      }
      if (pending) {
        deps.sendResult(
          upload,
          approvalFailure('busy', APPROVAL_MESSAGES.busy)
        );
        return;
      }
      const waitMs = upload.expiresAt - RESULT_DEADLINE_MARGIN_MS - Date.now();
      if (waitMs <= 0) {
        deps.sendResult(upload, noTime());
        return;
      }
      const request: Pending = {
        requestId: valid.requestId,
        upload,
        allowWindow: valid.allowWindow,
        meta,
        timer: undefined,
        onAbort: () => {
          // The connection that asked is gone: settle so the card closes and a new request isn't `busy`.
          if (pending === request) finish(ended);
        },
      };
      meta.signal.addEventListener('abort', request.onAbort);
      request.timer = setTimeout(() => finish(noTime), waitMs);
      pending = request;
      cards = [
        ...cards,
        { ...valid, toolCallId: frame.toolCallId, outcome: 'pending' },
      ];
      state = { status: 'pending', ...valid };
      notify();
    },

    approveOnce() {
      finish(grant('once'));
    },

    approveWindow() {
      if (!pending?.allowWindow) return;
      finish(grant('window'));
    },

    decline() {
      finish(() => approvalFailure('declined'));
    },

    cancel() {
      finish(() => approvalFailure('declined'), 'cancelled');
    },

    end() {
      finish(ended);
    },

    sessionEnded(meta) {
      endedSessions.add(meta.signal);
      finish(ended);
    },

    sessionStarted(meta) {
      endedSessions.delete(meta.signal);
    },

    dispose() {
      finish(ended);
    },

    getState: () => state,

    getCards: () => cards,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export interface ApprovalOperationView {
  /** Stable React key within its plan. */
  id: string;
  action: string;
  kind: string;
  key: string;
  detail?: string;
  /** The server's account of what a destructive operation removes. */
  effect?: ApprovalEffect;
  label?: string;
  /** The target's own api name, only when it has no label. */
  apiName?: string;
  parentLabel?: string;
  parentKey?: string;
  typeLabel?: string;
  /** Destructive per the server, or a delete. */
  emphasized: boolean;
}

export interface ApprovalPlanView {
  planId: string;
  summary: string;
  operations: ApprovalOperationView[];
  counts: { total: number; destructive: number };
  /** Operations the server left out of `operations`; 0 when none were truncated. */
  moreCount: number;
}

export type ApprovalView = {
  showWindowOption: boolean;
  windowMinutes: number;
} & (
  | { kind: 'plans'; plans: ApprovalPlanView[] }
  | { kind: 'command'; commandLine: string }
);

/** Shell-style quoting, so each argument's boundaries show (display only). */
const quoteArg = (arg: string) =>
  /^[\w@%+=:,./-]+$/.test(arg) ? arg : `'${arg.replaceAll("'", `'\\''`)}'`;

/** What the card shows for a pending request. */
export const approvalView = ({
  allowWindow,
  windowMinutes,
  request,
}: Omit<ValidApprovalRequest, 'requestId'>): ApprovalView => {
  const options = { showWindowOption: allowWindow, windowMinutes };
  if (request.kind === 'command') {
    const commandLine = request.argv.map(quoteArg).join(' ');
    return { ...options, kind: 'command', commandLine };
  }
  return {
    ...options,
    kind: 'plans',
    plans: request.plans.map(({ planId, summary, operations, counts }) => ({
      planId,
      summary,
      counts,
      moreCount: Math.max(0, counts.total - operations.length),
      operations: operations.map((op, index) => ({
        id: `${index}:${op.action}:${op.kind}:${op.key}`,
        action: op.action,
        kind: op.kind,
        key: op.key,
        ...(op.detail === undefined ? {} : { detail: op.detail }),
        ...(op.effect === undefined ? {} : { effect: op.effect }),
        ...(op.label === undefined ? {} : { label: op.label }),
        ...(op.apiName === undefined ? {} : { apiName: op.apiName }),
        ...(op.parentLabel === undefined
          ? {}
          : { parentLabel: op.parentLabel }),
        ...(op.parentKey === undefined ? {} : { parentKey: op.parentKey }),
        ...(op.typeLabel === undefined ? {} : { typeLabel: op.typeLabel }),
        emphasized: op.destructive || op.action === 'delete',
      })),
    })),
  };
};

export interface KiCodeApprovalWiring {
  controller: ApprovalController;
  /** Synchronous: true while a card waits, so a send re-checks it after any await. */
  isSendBlocked(): boolean;
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  dispose(): void;
}

export const createKiCodeApproval = (
  deps: ApprovalControllerDeps
): KiCodeApprovalWiring => {
  const controller = createApprovalController(deps);
  return {
    controller,
    isSendBlocked: () => controller.getState().status === 'pending',
    handleServerMessage: (message, meta) => {
      if (message.type === 'session.ready') controller.sessionStarted(meta);
      if (message.type === 'ui.approval') controller.handle(message, meta);
      // A session that closed, or ended on an error the chat treats as closing
      // it, takes no more requests on this connection.
      if (
        message.type === 'session.closed' ||
        (message.type === 'error' &&
          (SESSION_ENDING_ERROR_CODES as readonly string[]).includes(
            message.code
          ))
      ) {
        controller.sessionEnded(meta);
      }
      // A settled turn no longer waits on the card.
      if (message.type === 'turn.settled') controller.end();
    },
    dispose: () => controller.dispose(),
  };
};
